<template>
  <LayoutPage title="Smart Units" :loading="smartUnitsLoading">
    <template #actions>
      <div class="mr-4" style="min-width: 240px">
        <SearchField v-model="tableState.search" />
      </div>
      <PossiblePMS v-slot="{ disabled }">
        <v-btn
          color="success"
          class="action-btn"
          :disabled="disabled"
          :to="{ name: 'AddSmartUnit', query: { subtype: 'unit' } }"
        >
          Create New Smart Unit
        </v-btn>
      </PossiblePMS>
    </template>

    <div v-if="isAutoEnrollment" class="d-flex mb-4" style="gap: 8px">
      <v-btn
        small
        :color="activeFilter === 'all' ? 'primary' : ''"
        :outlined="activeFilter !== 'all'"
        @click="activeFilter = 'all'"
      >
        All
      </v-btn>
      <v-btn
        small
        :color="activeFilter === 'awaiting_device' ? 'warning' : ''"
        :outlined="activeFilter !== 'awaiting_device'"
        @click="activeFilter = 'awaiting_device'"
      >
        <v-icon left small>mdi-alert</v-icon>
        Assign Tenant Device ({{ awaitingDeviceCount }})
      </v-btn>
      <v-btn
        small
        :color="activeFilter === 'active' ? 'success' : ''"
        :outlined="activeFilter !== 'active'"
        @click="activeFilter = 'active'"
      >
        Active ({{ activeCount }})
      </v-btn>
    </div>

    <v-skeleton-loader
      :loading="smartUnitsLoading"
      type="table-thead, table-row-divider@10"
    >
      <v-data-table
        :headers="dynamicHeaders"
        :items="filteredSmartUnits"
        :custom-filter="customFilter"
        :custom-sort="customSort"
        :search="tableState.search"
        :sort-by.sync="tableState.sortBy"
        :sort-desc.sync="tableState.sortDesc"
        :page.sync="tableState.page"
        :items-per-page.sync="tableState.itemsPerPage"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
        :item-class="rowClass"
      >
        <template #item.actions="{ item }">
          <ButtonView @click="gotoUnit(item)" />
        </template>

        <template #item.name="{ item }">
          <div class="d-flex justify-space-between align-center">
            <PossiblePMS as-icon :sync-obj="item">
              {{ item.name }}
            </PossiblePMS>
            <v-chip
              v-if="shouldShowProLabel(item)"
              x-small
              color="#1565c0"
              text-color="white"
              class="ml-1"
            >
              <v-icon left small color="#ffd700">mdi-crown</v-icon> PRO
            </v-chip>
            <ScheduledUnitBadge
              :scheduled="isScheduled(item)"
              :move-in-at="item.scheduledMoveInAt"
              :time-zone="item.timeZone || facilityTimeZone"
            />
          </div>
        </template>

        <template #item.document="{ item }">
          <DocumentDialog :unit="item" />
        </template>

        <template #item.moveInAt="{ item }">
          <span v-if="moveInDateFor(item)">
            {{ formatDate(moveInDateFor(item)) }}
          </span>
          <span v-else class="grey--text">—</span>
        </template>

        <template #item.devicesName="{ item }">
          <template
            v-if="
              (isAutoEnrollment && isAwaitingDevice(item)) ||
              (!isAutoEnrollment && !(item.devices && item.devices.length))
            "
          >
            <div class="d-flex align-center" style="gap: 12px">
              <AwaitingDeviceDaysPill
                v-if="isAutoEnrollment"
                :days="daysWithoutDevice(item)"
              />
              <v-btn small color="success" @click="assignDevice(item)">
                Assign
              </v-btn>
            </div>
          </template>
          <template v-else>
            <v-list dense color="transparent">
              <v-list-item
                v-for="(device, index) in item.devicesName"
                :key="index"
                class="ml-0 pl-0"
              >
                <v-list-item-content>
                  {{ device }}
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </template>
        </template>

        <template #item.devicesHealth="{ item }">
          <v-list dense color="transparent">
            <v-list-item
              v-for="device in item.devices"
              :key="device.id"
              class="mx-0 px-0"
            >
              <v-list-item-icon class="mx-0">
                <DeviceGaugeCluster :device="device" />
              </v-list-item-icon>
            </v-list-item>
          </v-list>
        </template>
        <template #item.temperature="{ item }">
          <v-list dense color="transparent">
            <v-list-item
              v-for="device in item.devices"
              :key="device.id"
              class="mx-0 px-0"
            >
              <div
                v-if="device.temperature"
                :class="`${smartUnitTemperatureColor(
                  device.temperature
                )}--text`"
              >
                {{ convertCelsiusToFahrenheit(device.temperature) }}℉
              </div>
              <div v-else class="grey--text">N/A</div>
            </v-list-item>
          </v-list>
        </template>
        <template #item.humidity="{ item }">
          <v-list dense color="transparent">
            <v-list-item
              v-for="device in item.devices"
              :key="device.id"
              class="mx-0 px-0"
            >
              <div
                v-if="device.humidity"
                :class="`${smartUnitHumidityColor(device.humidity)}--text`"
              >
                {{ roundValue(device.humidity) }}%
              </div>
              <div v-else class="grey--text">N/A</div>
            </v-list-item>
          </v-list>
        </template>

        <template #item.incident="{ item }">
          <MotionIncidentIndicator
            :scheduled="isScheduled(item)"
            :has-incident="(item.incidents || []).length > 0"
            @resolve="gotoFirstIncident(item)"
          />
        </template>

        <template #item.zone="{ item }">
          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <a
                class="zone-link"
                :class="
                  isUnitAssigned(item) ? 'zone-assigned' : 'zone-unassigned'
                "
                v-bind="attrs"
                v-on="on"
                @click.stop="openZoneDialog(item)"
              >
                <span v-if="isUnitAssigned(item)">{{ item.parent.name }}</span>
                <span v-else>Unassigned</span>
              </a>
            </template>
            <span>{{
              isUnitAssigned(item) ? "Change the zone" : "Assign to zone"
            }}</span>
          </v-tooltip>
        </template>

        <template #footer.prepend>
          <ButtonExportCSV
            class="order-last"
            :table-data="smartUnits"
            :headers="dynamicHeaders"
            filename="SD_Smart_Units"
          />
          <ButtonPrint
            class="order-last"
            :before-print="showAllItems"
            :after-print="restoreItemsPerPage"
          />
          <span
            v-if="isAutoEnrollment"
            class="caption grey--text ml-2 order-last"
          >
            Days Without Device counts up from move-in date when no device is
            assigned
          </span>
        </template>
      </v-data-table>
    </v-skeleton-loader>

    <DialogUpdateZone
      v-if="zoneDialogUnit"
      :unit-id="zoneDialogUnit.id"
      :unit-name="zoneDialogUnit.name"
      :facility-id="currentFacilityId"
      :current-zone-id="
        zoneDialogUnit.parent && zoneDialogUnit.parent.subtype === 'zone'
          ? zoneDialogUnit.parent.id
          : null
      "
      @saved="handleZoneSaved"
      @close="zoneDialogUnit = null"
    />
  </LayoutPage>
</template>

<script>
import DeviceGaugeCluster from "@atoms/DeviceGaugeCluster.vue";
import SearchField from "@atoms/SearchField.vue";
import ButtonView from "@atoms/ButtonView.vue";
import PossiblePMS from "@components/common/PossiblePMS.vue";
import DocumentDialog from "../documents/DocumentDialog.vue";
import LayoutPage from "@layout/LayoutPage.vue";
import ButtonPrint from "@atoms/ButtonPrint.vue";
import ButtonExportCSV from "@atoms/ButtonExportCSV.vue";
import DialogUpdateZone from "../common/DialogUpdateZone.vue";

import { SMART_UNITS } from "./graphql";
import useFacility from "../authentication/useFacility";
import useDataTable from "@components/common/useDataTable";
import usePMS from "@components/common/usePMS";
import { extractDevicesFields } from "@components/common/helpers/devices";
import { stringSortCollator, titleCase } from "@tod-ui/helpers/strings";
import { smartUnitDocument } from "../documents/helper";
import { displayUnitType } from "@tod-ui/helpers/unitType";
import { computed } from "vue";
import { convertCelsiusToFahrenheit } from "@tod-ui/helpers/utils";
import { roundValue } from "@tod-ui/helpers/math";
import {
  isAwaitingDevice,
  isScheduled,
  smartUnitHumidityColor,
  smartUnitTemperatureColor,
} from "./helper";
import AwaitingDeviceDaysPill from "./AwaitingDeviceDaysPill.vue";
import ScheduledUnitBadge from "./ScheduledUnitBadge.vue";
import MotionIncidentIndicator from "./MotionIncidentIndicator.vue";
import { formatDate } from "@tod-ui/helpers/datetime";

export default {
  name: "SmartUnits",
  components: {
    DeviceGaugeCluster,
    SearchField,
    ButtonView,
    DocumentDialog,
    PossiblePMS,
    LayoutPage,
    ButtonPrint,
    ButtonExportCSV,
    AwaitingDeviceDaysPill,
    ScheduledUnitBadge,
    MotionIncidentIndicator,
    DialogUpdateZone,
  },
  setup() {
    const {
      currentFacilityId,
      currentFacility,
      facilityFeatureEnabled,
      facilityTimeZone,
    } = useFacility();
    const {
      hasPMS,
      pmsServiceHasDocuments,
      propertyManagementSystem,
      isAutoEnrollment,
    } = usePMS();
    const { tableState, showAllItems, restoreItemsPerPage } = useDataTable({
      itemsPerPage: 10,
      sortBy: ["name"],
    });
    const showDocuments = computed(
      () =>
        facilityFeatureEnabled("facility_integrated_esign") ||
        pmsServiceHasDocuments.value
    );
    const smartZonesEnabled = computed(() =>
      facilityFeatureEnabled("smart_zones")
    );
    return {
      currentFacilityId,
      currentFacility,
      facilityTimeZone,
      hasPMS,
      isAutoEnrollment,
      showDocuments,
      smartZonesEnabled,
      tableState,
      showAllItems,
      restoreItemsPerPage,
      roundValue,
      pmsServiceHasDocuments: computed(() => pmsServiceHasDocuments.value),
    };
  },
  data: () => ({
    smartUnits: [],
    activeFilter: "all",
    zoneDialogUnit: null,
  }),
  computed: {
    smartUnitsLoading() {
      return this.$apollo.queries.smartUnits.loading;
    },
    // In RevMax mode: only show units the RevMax flow owns — those with a device OR
    // awaiting assignment. Legacy enrollments from a previous mode (no device, no
    // enrollment_info) are not part of RevMax tracking and are hidden here.
    revmaxVisibleUnits() {
      if (!this.isAutoEnrollment) return this.smartUnits;
      return this.smartUnits.filter(
        (u) => (u.devices && u.devices.length > 0) || isAwaitingDevice(u)
      );
    },
    awaitingRevmaxUnits() {
      return this.revmaxVisibleUnits.filter(isAwaitingDevice);
    },
    activeRevmaxUnits() {
      return this.revmaxVisibleUnits.filter((u) => !isAwaitingDevice(u));
    },
    awaitingDeviceCount() {
      return this.awaitingRevmaxUnits.length;
    },
    activeCount() {
      return this.activeRevmaxUnits.length;
    },
    filteredSmartUnits() {
      if (!this.isAutoEnrollment) return this.smartUnits;
      if (this.activeFilter === "awaiting_device")
        return this.awaitingRevmaxUnits;
      if (this.activeFilter === "active") return this.activeRevmaxUnits;
      return this.revmaxVisibleUnits;
    },
    headers() {
      const base = [
        {
          text: "Details",
          align: "start",
          filterable: false,
          sortable: false,
          class: "action-column",
          cellClass: "action-column",
          value: "actions",
        },
        { text: "Smart Unit #", value: "name" },
        {
          text: "Unit Type",
          value: "unitTypeName",
        },
        {
          text: "Unit Size",
          value: "unitTypeSize",
        },
        {
          text: "Zone",
          value: "zone",
          sortable: false,
        },
        { text: "First Name", value: "responderFirstName" },
        { text: "Last Name", value: "responderLastName" },
        {
          text:
            !this.pmsServiceHasDocuments && this.hasPMS
              ? "Manual Send Addendum"
              : "Document Status",
          align: "center",
          value: "document",
          toString: (document) => titleCase(document.status),
        },
        { text: "Assigned Devices", value: "devicesName" },
        {
          text: "Device Health",
          filterable: false,
          sortable: false,
          class: "non-export",
          cellClass: "non-export",
          value: "devicesHealth",
        },
        { text: "Temperature °F", value: "temperature" },
        { text: "Humidity", value: "humidity" },
        {
          text: "Motion Incident",
          class: "action-column",
          cellClass: "action-column",
          value: "incident",
        },
      ];

      const moveInColumn = {
        text: "Move-In Date",
        value: "moveInAt",
        sortable: false,
      };
      const anchorIdx = base.findIndex((h) => h.value === "document");
      if (anchorIdx >= 0) {
        base.splice(anchorIdx, 0, moveInColumn);
      } else {
        base.push(moveInColumn);
      }

      return base;
    },
    dynamicHeaders() {
      return this.headers.filter(
        (header) =>
          (header.value !== "document" || this.showDocuments) &&
          header.value !== "zone"
      );
    },
  },
  apollo: {
    smartUnits: {
      query: SMART_UNITS,
      variables() {
        return { id: this.currentFacilityId };
      },
      update({ smartUnits }) {
        return smartUnits
          .reduce((acc, unit) => {
            const unitTypeName = displayUnitType(unit?.unitType, "type");
            const unitTypeSize = displayUnitType(unit?.unitType, "size");

            const billingAccount =
              unit?.billingAccounts?.length && unit.billingAccounts[0];

            const billingUser = billingAccount?.users?.length
              ? billingAccount.users[0]
              : null;

            const responderFirstName =
              unit.responders?.[0]?.user?.firstName ||
              billingUser?.firstName ||
              "(no recipient)";
            const responderLastName =
              unit.responders?.[0]?.user?.lastName ||
              billingUser?.lastName ||
              "";

            if (billingAccount?.status !== "ended_service") {
              // find the esign document corresponding to the unit
              const document = smartUnitDocument(
                billingAccount.documents,
                unit.id,
                this.hasPMS
              );
              acc.push({
                ...unit,
                unitTypeName,
                unitTypeSize,
                responderFirstName,
                responderLastName,
                billingAccount,
                document,
                ...extractDevicesFields(unit.devices),
              });
            }
            return acc;
          }, [])
          .sort((a, b) =>
            a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1
          );
      },
    },
  },
  methods: {
    isAwaitingDevice,
    isScheduled,
    isUnitAssigned(item) {
      return item.parent && item.parent.subtype === "zone";
    },
    openZoneDialog(item) {
      this.zoneDialogUnit = item;
    },
    handleZoneSaved() {
      this.zoneDialogUnit = null;
      this.$apollo.queries.smartUnits.refetch();
    },
    daysWithoutDevice(item) {
      return item.enrollmentInfo?.daysWithoutDevice ?? null;
    },
    moveInDateFor(item) {
      if (this.isAutoEnrollment) return item.enrollmentInfo?.moveInAt;
      return item.enrollmentInfo?.moveInAt || item.billingAccount?.insertedAt;
    },
    rowClass(item) {
      return this.isAutoEnrollment && isAwaitingDevice(item)
        ? "revmax-awaiting-device-row"
        : "";
    },
    formatDate,
    customFilter(value, search, item) {
      if (!search) return true;
      // NOTE: item is an `organization`
      let _search = search.toLowerCase();

      // Filter on `name`
      if (item.name.toLowerCase().indexOf(_search) >= 0) return true;

      // Filter on `responder`
      if (
        item.responderFirstName &&
        item.responderFirstName.toLowerCase().indexOf(_search) >= 0
      )
        return true;

      if (
        item.responderLastName &&
        item.responderLastName.toLowerCase().indexOf(_search) >= 0
      )
        return true;

      // Filter on `devices`
      const devices = item.devicesName.join(" ").toLowerCase();
      if (devices.indexOf(_search) >= 0) return true;

      if (item.unitTypeName.toLowerCase().indexOf(_search) >= 0) return true;
      if (item.unitTypeSize.toLowerCase().indexOf(_search) >= 0) return true;
    },
    shouldShowProLabel(item) {
      try {
        if (!item || !item.profile) {
          return false;
        }
        const facility = this.currentFacility?.value || this.currentFacility;
        if (!facility || !facility.profile) {
          return false;
        }
        const facilityProfile = facility.profile;
        const isUnitLevelEnabled =
          facilityProfile?.muteAlertsDuring === "day" &&
          (facilityProfile?.muteAlertsOrganizationType ?? "facility") ===
            "zone";

        return isUnitLevelEnabled && item.profile?.muteAlertsDuringDay === true;
      } catch (error) {
        console.warn("shouldShowProLabel error:", error);
        return false;
      }
    },
    customSort(items, sortBy, sortDesc) {
      // sortBy and sortDesc are arrays
      const _sortBy = sortBy[0];
      const _sortDesc = sortDesc[0] ? -1 : 1;

      items.sort((a, b) => {
        if (_sortBy === "document") {
          const aName = a.document.status;
          const bName = b.document.status;
          return (bName < aName ? -1 : 1) * _sortDesc;
        } else if (_sortBy === "devicesName") {
          const aDevices = a.devicesName
            .sort((da, db) => stringSortCollator.compare(da, db) * _sortDesc)
            .join(" ");
          const bDevices = b.devicesName
            .sort((da, db) => stringSortCollator.compare(da, db) * _sortDesc)
            .join(" ");

          return stringSortCollator.compare(aDevices, bDevices) * _sortDesc;
        } else {
          // Default sort comparison
          if (typeof a[_sortBy] === "string") {
            return (
              stringSortCollator.compare(a[_sortBy], b[_sortBy]) * _sortDesc
            );
          }
          return (b[_sortBy] < a[_sortBy] ? -1 : 1) * _sortDesc;
        }
      });

      return items;
    },
    gotoFirstIncident(unit) {
      if (!unit?.incidents?.length) return;

      this.$router.push({
        name: "Incident",
        params: {
          incident_id: unit.incidents[0].id,
        },
      });
    },
    gotoUnit(unit) {
      const renterId = unit.billingAccounts?.[0]?.users?.[0]?.id;
      if (renterId) {
        this.$router.push({
          name: "AssignedSmartUnitDrilldown",
          params: { renter_id: renterId, zone_id: unit.id },
        });
      } else {
        this.$router.push({
          name: "ManageSmartUnitDevices",
          params: { zone_id: unit.id },
        });
      }
    },
    assignDevice(unit) {
      this.$router.push({
        name: "ManageSmartUnitDevices",
        params: { zone_id: unit.id },
      });
    },
    convertCelsiusToFahrenheit,
    smartUnitHumidityColor,
    smartUnitTemperatureColor,
  },
};
</script>

<style scoped>
::v-deep .revmax-awaiting-device-row {
  background-color: #ffebee !important;
}

/* Zone cell renders as plain text; the link affordance appears on hover.
   Color differentiates an assigned zone from an unassigned unit. */
.zone-link {
  cursor: pointer;
  text-decoration: none;
  font-weight: normal;
  white-space: nowrap;
}

/* Assigned zones render as an amber tile; unassigned stays plain red text. */
.zone-link.zone-assigned {
  display: inline-block;
  padding: 2px 8px;
  border-left: 2px solid #ffb300;
  background: #fffde7;
  border-radius: 2px;
  color: inherit;
}

.zone-link.zone-unassigned {
  color: #e53935;
}

.zone-link:hover {
  color: #1976d2;
  text-decoration: underline;
}
</style>
